import { CORE_API_BASE as API_URL } from "#app/lib/api-bases";
import { getActiveWorkspaceId } from "#app/lib/data/data-provider";
import { at } from "#app/lib/utils/at";

/**
 * Permission-gated photo delivery (note photos). The core API serves these
 * at GET /media/:id/content after checking the requester can view the
 * photo's note/issue — there is no public CDN URL. Clients therefore fetch
 * the bytes with their bearer token and render object URLs.
 *
 * Object URLs are cached per photo for the session: note lists re-render
 * constantly and the images are immutable (a photo id never changes bytes).
 */

export type PhotoVariant = "full" | "thumb";

/**
 * Canonical src persisted in note HTML — RELATIVE, so stored notes don't
 * bake in an environment's API host. Renderers resolve it through
 * resolvePhotoHtml before display; it is never fetched as-is.
 */
export function mediaContentSrc(photoId: string): string {
  return `/media/${photoId}/content`;
}

// Matches the persisted relative form and (defensively) this environment's
// absolute form. Also accepts `/photos/:id/content`: notes authored before the
// photo→media rename carry that src in their stored HTML, the id is unchanged
// by the rename, and there is no `/photos/` route — without this those inline
// images would 404 forever.
const CONTENT_URL_PATTERN = new RegExp(
  `^(?:${API_URL.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})?/(?:media|photos)/([^/]+)/content$`,
);

/** Extracts the photo id from a content-route URL/src, or null. */
export function mediaIdFromContentUrl(url: string): string | null {
  const match = CONTENT_URL_PATTERN.exec(url);
  // Group 1 is not optional, so a match always carries the id.
  return match ? at(match, 1, "media id") : null;
}

/** Reverse lookup for a session object URL this module created/registered
 * (rendered inline images carry these as src), or null. */
export function mediaIdFromObjectUrl(url: string): string | null {
  return reverseMap.get(url) ?? null;
}

/** Photo ids referenced by a note HTML's inline images, in document order. */
export function mediaIdsFromHtml(html: string): string[] {
  const ids: string[] = [];
  for (const match of html.matchAll(IMG_SRC_PATTERN)) {
    const id = mediaIdFromContentUrl(at(match, 2, "img src"));
    if (id) ids.push(id);
  }
  return ids;
}

interface FetchAuth {
  getAccessToken: () => Promise<string | null>;
}

// `${photoId}:${variant}` → resolved object URL (or in-flight promise —
// concurrent renders of the same photo share one fetch).
const objectUrlCache = new Map<string, Promise<string>>();
// objectURL → photoId (full variant only), for rewriting editor HTML back
// to canonical URLs.
const reverseMap = new Map<string, string>();

// Bound the session cache so a long browsing session (many issues / media
// tabs) can't accumulate decoded blob memory without limit. LRU over photo ids
// — both variants of a photo evict together, so we never revoke a URL the
// sibling variant still shares. Active tiles re-touch their photo on every
// resolve, so only photos that have scrolled out of use are evicted; a later
// re-view simply re-fetches.
const MAX_CACHED_PHOTOS = 200;
// Photo ids in least→most-recently-used order (Set iteration = insertion
// order; touch deletes + re-adds to move the id to the end).
const lruPhotos = new Set<string>();

function evictPhoto(photoId: string): void {
  for (const variant of ["full", "thumb"] as const) {
    const key = `${photoId}:${variant}`;
    const entry = objectUrlCache.get(key);
    if (!entry) continue;
    objectUrlCache.delete(key);
    entry.then(
      (url) => {
        URL.revokeObjectURL(url);
        reverseMap.delete(url);
      },
      () => undefined,
    );
  }
}

function touchPhoto(photoId: string): void {
  lruPhotos.delete(photoId);
  lruPhotos.add(photoId);
  if (lruPhotos.size <= MAX_CACHED_PHOTOS) return;
  for (const oldest of lruPhotos) {
    if (lruPhotos.size <= MAX_CACHED_PHOTOS) break;
    lruPhotos.delete(oldest);
    evictPhoto(oldest);
  }
}

// ── batched URL minting ────────────────────────────────────────────────
// A note feed or media tab renders many tiles at once. Minting each photo's
// presigned URL with its own authenticated (CORS-preflighted) request is an
// N+1 — both in HTTP round trips and in the per-request DB work + issue
// permission check on the server. Instead we coalesce every mint requested in
// the same tick into ONE POST /media/content-urls. Effects for all mounted
// tiles run synchronously in a single commit, so they all enqueue before the
// microtask flush fires.

interface PendingMint {
  photoId: string;
  variant: PhotoVariant;
  // Captured at enqueue time, NOT read at flush time: a workspace switch (or
  // re-auth) landing mid-tick can enqueue photos from two identities in one
  // flush, and each must be minted under the identity/workspace that requested
  // it — the server gates per-issue access by workspace.
  workspaceId: string | null;
  auth: FetchAuth;
  resolveUrl: (presignedUrl: string) => void;
  rejectUrl: (err: unknown) => void;
}

// The server explicitly omitted this photo from the response — not authorized
// or missing. A STABLE answer (unlike a network/5xx blip), so the caller
// caches the rejection instead of re-POSTing on every tile remount.
export class PhotoNotAuthorizedError extends Error {}

// Largest item set per POST. A media tab can mount hundreds of tiles in one
// commit; a single giant request would gate every tile on the slowest lookup
// and risk an oversized body. Chunk so tiles paint progressively.
const MINT_BATCH_SIZE = 50;

let mintQueue: PendingMint[] = [];
let mintScheduled = false;

function scheduleMintFlush(): void {
  if (mintScheduled) return;
  mintScheduled = true;
  // A resolved-promise microtask, not queueMicrotask — widest engine support.
  void Promise.resolve().then(flushMintQueue);
}

async function flushMintQueue(): Promise<void> {
  const queued = mintQueue;
  mintQueue = [];
  mintScheduled = false;
  if (!queued.length) return;
  // Partition by workspace (see PendingMint.workspaceId), then chunk each
  // partition to MINT_BATCH_SIZE. Every resulting POST carries one workspace's
  // photos under that workspace's header.
  const byWorkspace = new Map<string, PendingMint[]>();
  for (const p of queued) {
    const key = p.workspaceId ?? "";
    const group = byWorkspace.get(key);
    if (group) group.push(p);
    else byWorkspace.set(key, [p]);
  }
  await Promise.all(
    [...byWorkspace.values()].flatMap((group) => {
      const chunks: Promise<void>[] = [];
      for (let i = 0; i < group.length; i += MINT_BATCH_SIZE) {
        chunks.push(mintBatch(group.slice(i, i + MINT_BATCH_SIZE)));
      }
      return chunks;
    }),
  );
}

// Mints one (already partitioned + size-capped) batch — all items share a
// workspace and auth.
async function mintBatch(batch: PendingMint[]): Promise<void> {
  try {
    // The caller slices batches out of a non-empty group, so the first item
    // is what carries the shared workspace and auth for the whole request.
    const head = at(batch, 0, "batch item");
    const token = await head.auth.getAccessToken();
    const workspaceId = head.workspaceId;
    // The authed request is CORS-preflighted (custom headers); browsers refuse
    // cross-origin redirects on preflighted requests, so the route returns the
    // presigned URLs as JSON and each photo's bytes come from a plain
    // headerless GET (no preflight) below.
    const res = await fetch(`${API_URL}/media/content-urls`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(workspaceId ? { workspaceid: workspaceId } : {}),
      },
      body: JSON.stringify({
        items: batch.map((p) => ({
          id: p.photoId,
          size: p.variant === "thumb" ? "thumb" : undefined,
        })),
      }),
    });
    if (!res.ok) throw new Error(`Photo URL mint failed (${res.status})`);
    const { urls, retryable } = (await res.json()) as {
      urls: Record<string, { url: string }>;
      retryable?: string[];
    };
    const retrySet = new Set(retryable ?? []);
    for (const p of batch) {
      const key = `${p.photoId}:${p.variant}`;
      const entry = urls[key];
      if (entry?.url) p.resolveUrl(entry.url);
      // Listed in `retryable` = a TRANSIENT miss (photo/link not resolvable
      // yet — e.g. an attach still committing). Reject with a generic Error so
      // the caller DROPS the cache entry and retries on the next render.
      else if (retrySet.has(key))
        p.rejectUrl(new Error(`Photo ${p.photoId} not available yet`));
      // Otherwise omitted = a stable denial, so reject with
      // PhotoNotAuthorizedError (negatively cached by the caller) rather than a
      // generic Error, to stop forbidden tiles re-POSTing on every render.
      else
        p.rejectUrl(
          new PhotoNotAuthorizedError(
            `Photo ${p.photoId} not authorized or missing`,
          ),
        );
    }
  } catch (err) {
    // Whole-batch failure (network, 5xx, expired token) — transient. Reject so
    // the caller drops the cache entry and retries on the next render.
    for (const p of batch) p.rejectUrl(err);
  }
}

/**
 * Mints the permission-checked presigned GET URL WITHOUT fetching the bytes —
 * for media fed straight to the browser (a <video> element that range-streams,
 * or window.open for a PDF). Video full URLs carry a session-length TTL
 * server-side, so they survive seeks. Not cached: each call mints fresh.
 */
export function resolveMediaPresignedUrl(
  photoId: string,
  auth: FetchAuth,
  variant: PhotoVariant = "full",
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    mintQueue.push({
      photoId,
      variant,
      workspaceId: getActiveWorkspaceId(),
      auth,
      resolveUrl: resolve,
      rejectUrl: reject,
    });
    scheduleMintFlush();
  });
}

export async function resolveMediaObjectUrl(
  photoId: string,
  auth: FetchAuth,
  variant: PhotoVariant = "full",
): Promise<string> {
  const cacheKey = `${photoId}:${variant}`;
  const cached = objectUrlCache.get(cacheKey);
  if (cached) {
    touchPhoto(photoId);
    return cached;
  }

  const promise = (async () => {
    const presignedUrl = await new Promise<string>((resolve, reject) => {
      mintQueue.push({
        photoId,
        variant,
        // Snapshot identity/workspace now — the flush runs a microtask later.
        workspaceId: getActiveWorkspaceId(),
        auth,
        resolveUrl: resolve,
        rejectUrl: reject,
      });
      scheduleMintFlush();
    });
    const response = await fetch(presignedUrl);
    if (!response.ok) throw new Error(`Photo fetch failed (${response.status})`);
    const objectUrl = URL.createObjectURL(await response.blob());
    if (variant === "full") reverseMap.set(objectUrl, photoId);
    return objectUrl;
  })();

  // Drop TRANSIENT failures (expired token, flaky network) from the cache so
  // they retry on the next render. Keep a PhotoNotAuthorizedError cached: it's
  // a stable "you can't see this / it's gone" answer, so caching the rejection
  // stops forbidden tiles from re-POSTing on every remount (a virtualized feed
  // scrolling one in and out). clearMediaObjectUrlCache wipes it on sign-out /
  // workspace switch, so a later access grant re-resolves cleanly.
  objectUrlCache.set(
    cacheKey,
    promise.catch((err) => {
      if (!(err instanceof PhotoNotAuthorizedError)) {
        objectUrlCache.delete(cacheKey);
      }
      throw err;
    }),
  );
  touchPhoto(photoId);
  return objectUrlCache.get(cacheKey)!;
}

/**
 * Drops every cached object URL and reverse-map entry, revoking the underlying
 * blob URLs. MUST be called on sign-out and workspace switch: the cache is
 * module-level and keyed only by photo id, so without this, permission-gated
 * bytes fetched under one identity/workspace would keep rendering for the next
 * user signed into the same SPA session (no page reload happens on sign-out),
 * bypassing the content route's per-photo permission check.
 */
export function clearMediaObjectUrlCache(): void {
  // Full-variant URLs are tracked in reverseMap; resolved thumb URLs only live
  // as settled promises — revoke both so no blob URL leaks.
  for (const url of reverseMap.keys()) URL.revokeObjectURL(url);
  for (const promise of objectUrlCache.values()) {
    promise.then((url) => URL.revokeObjectURL(url), () => undefined);
  }
  objectUrlCache.clear();
  reverseMap.clear();
  lruPhotos.clear();
}

/** Registers locally-created object URLs (fresh upload) so HTML restore and
 * future renders reuse them instead of re-fetching what we already have. */
export function registerMediaObjectUrl(
  photoId: string,
  objectUrl: string,
  thumbObjectUrl?: string,
): void {
  objectUrlCache.set(`${photoId}:full`, Promise.resolve(objectUrl));
  reverseMap.set(objectUrl, photoId);
  // Register the thumb variant ONLY when processing produced one. Pointing it
  // at the full bytes would cache a whole video/document blob (up to 200MB) as
  // a "thumbnail", and a tile asking for the thumb would decode the entire file
  // as an <Image>. With no entry, the thumb request resolves through the server,
  // which returns no thumbnail for a posterless non-image and the tile renders a
  // placeholder instead.
  if (thumbObjectUrl) {
    objectUrlCache.set(`${photoId}:thumb`, Promise.resolve(thumbObjectUrl));
  }
  touchPhoto(photoId);
}

const IMG_SRC_PATTERN = /(<img\b[^>]*\bsrc=")([^"]+)(")/gi;

/**
 * Display/edit direction: swaps content-route srcs in note HTML for
 * authenticated object URLs. Async — fetches any photo not yet cached.
 */
export async function resolvePhotoHtml(
  html: string,
  auth: FetchAuth,
): Promise<string> {
  const ids = new Map<string, string>(); // src → photoId
  for (const match of html.matchAll(IMG_SRC_PATTERN)) {
    const src = at(match, 2, "img src");
    const id = mediaIdFromContentUrl(src);
    if (id) ids.set(src, id);
  }
  if (!ids.size) return html;

  const resolved = new Map<string, string>();
  await Promise.all(
    Array.from(ids.entries()).map(async ([src, id]) => {
      try {
        resolved.set(src, await resolveMediaObjectUrl(id, auth));
      } catch {
        // Leave the canonical URL in place — renders as a broken image
        // rather than dropping the node.
      }
    }),
  );
  return html.replace(IMG_SRC_PATTERN, (whole, pre, src, post) => {
    const objectUrl = resolved.get(src);
    return objectUrl ? `${pre}${objectUrl}${post}` : whole;
  });
}

/**
 * Save direction: swaps known object URLs back to canonical content-route
 * URLs before the HTML is persisted. Synchronous — only URLs this session
 * created or resolved can appear in the editor.
 */
export function restoreMediaHtml(html: string): string {
  return html.replace(IMG_SRC_PATTERN, (whole, pre, src, post) => {
    const photoId = reverseMap.get(src);
    return photoId ? `${pre}${mediaContentSrc(photoId)}${post}` : whole;
  });
}
